import { SyntheticEvent, useCallback, useState } from 'react';
import { AnswerRequest, DeclineModalInfo, QuestionRequest } from '../types';
import { useAppDispatch } from "../store/store";
import { getQuestion, saveAnswer, submitAnswerAndGetQuestion } from "../store/survey/surveySlice";
import { useNavigate } from 'react-router';
import { ROUTES } from '../config/routes';

// Import specialized hooks (assumed to be available in the file system)
import { useSurveyData } from './useSurveyData';
import { useAnswerInput } from './useAnswerInput';
import { useDeclineModal } from './useDeclineModal';

interface UseSurveyHandlerReturn {
    selectedAnswers: (number)[];
    setSelectedAnswers: React.Dispatch<React.SetStateAction<number[]>>;
    errorMessage?: string | null;
    onRadioChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onQuestionSubmit: (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => Promise<void>;
    declineModalInfo: DeclineModalInfo | null;
    handleDeclineSubmit: () => Promise<void>;
    allAnswersDeclined?: boolean | null;
}

/**
 * @hook useSurveyHandler
 * @description Orchestrates state, data flow, and submission logic for a single survey question view.
 * It delegates core responsibilities to specialized hooks.
 */
export const useSurveyHandler = (): UseSurveyHandlerReturn => {
    // --- 1. LOCAL STATE & NAVIGATION (Primary Orchestrator State) ---
    // This state is reserved for API/system errors that should appear at the question level.
    const [externalErrorMessage, setExternalErrorMessage] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // --- 2. INTEGRATE SPECIALIZED HOOKS ---
    // Input Layer: Manages user selections and input change handlers
    const { 
        selectedAnswers, 
        setSelectedAnswers, 
        errorMessage: inputErrorMessage, 
        onRadioChange, 
        onCheckboxChange 
    } = useAnswerInput({ setErrorMessage: setExternalErrorMessage });
    
    // Data Layer: Handles Redux selection and answer initialization
    const { question, user, language, answersDeclined } = useSurveyData(
        // Pass callbacks to useSurveyData so it can reset/initialize local input state
        useCallback(setSelectedAnswers, []), 
        useCallback(setExternalErrorMessage, [])
    );
    


    // Business Logic Layer: Manages decline thresholds, messages, and decision logic
    const { 
        declineModalInfo, 
        setDeclineModalInfo, 
        checkAndTriggerDeclineModal, 
        isDeclineAnswer 
    } = useDeclineModal({ question, selectedAnswers });

    // Combine error messages: prioritize the immediate input error over system/API errors
    const finalErrorMessage = inputErrorMessage || externalErrorMessage;
    
    // --- 3. PAYLOAD FACTORY  ---
    /**
     * Factory function to consistently build AnswerRequest and QuestionRequest payloads.
     */
    const createPayloads = useCallback((answers: { [key: number]: string }, submitType: "Next" | "Previous") => {
        const commonData = {
            SessionId: user?.SessionId,
            ClientId: user?.ClientId,
            QuestionId: question?.QuestionId,
            QuestionOrder: question?.QuestionOrder,
            SiblingIndex: question?.SiblingIndex,
        };

        const answerData: Partial<AnswerRequest> = {
            ...commonData,
            Answers: answers,
            AllAnswersDeclined: answersDeclined, 
        };

        const questionData: Partial<QuestionRequest> = {
            ...commonData,
            SubmitType: submitType,
            Language: language ?? "EN",
        };

        return { answerData, questionData };
    }, [user, question, language, answersDeclined]);


    // --- 4. CORE HANDLERS (Submission Logic) ---

    const handleDeclineSubmit = useCallback(async () => {
        setDeclineModalInfo(null);
        setExternalErrorMessage(null);

        // Logic relies on isDeclineAnswer from the decline hook
        const declineKey = Object.keys(question?.Answers || {}).find(key => isDeclineAnswer(Number(key)));
        if (!declineKey) {
            setExternalErrorMessage("Decline option not found.");
            return;
        }

        const declineAnswer: { [key: number]: string } = {
            [Number(declineKey)]: question!.Answers![Number(declineKey)],
        };

        const { answerData, questionData } = createPayloads(declineAnswer, "Next");

        try {
            if (question?.IsFinalQuestion) {
                // Determine if all answers are now declined based on post-submission count
                const isAllDeclinedCheck = (question.DeclineCount || 0) + 1 >= (question.TotalNoQuestions || 0);

                await dispatch(saveAnswer({
                    ...answerData,
                    AllAnswersDeclined: isAllDeclinedCheck
                }));
                navigate(ROUTES.SURVEY_CONFIRM);

            } else {
                await dispatch(submitAnswerAndGetQuestion(answerData, questionData))
            }

        } catch (error) {
            console.error("Error handling decline submission:", error);
            navigate(ROUTES.ERROR)
        }
    }, [question, dispatch, navigate, createPayloads, isDeclineAnswer, setExternalErrorMessage, setDeclineModalInfo]);


    const onQuestionSubmit = useCallback(async (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
        event.preventDefault();

        const submitButton = event.nativeEvent.submitter as HTMLButtonElement | null;
        const actionType = submitButton?.value;

        // Helper function to format the selected answers
        const getUserAnswer = (): { [key: number]: string } => {
            if (!question?.Answers) return {};
            return selectedAnswers.reduce((acc, key) => {
                if (question.Answers![key]) {
                    acc[key] = question.Answers![key];
                }
                return acc;
            }, {} as { [key: number]: string });
        };
        const answers = getUserAnswer();

        // 💡 ORCHESTRATION POINT: Check if the decline modal should be triggered.
        if (checkAndTriggerDeclineModal(actionType)) {
            return; // Stop submission to display the modal
        }

        if (actionType === "Next") {
            // Input validation check (handled by useAnswerInput, but confirmed here)
            if (selectedAnswers.length === 0) {
                setExternalErrorMessage("Please select an answer.");
                return;
            }

            setExternalErrorMessage(null);

            // 1. Create Payloads
            const { answerData, questionData } = createPayloads(answers, "Next");

            try {
                if (question?.IsFinalQuestion) {
                    await dispatch(saveAnswer(answerData));
                    navigate(ROUTES.SURVEY_CONFIRM);
                } else {
                    await dispatch(submitAnswerAndGetQuestion(answerData, questionData));
                    setSelectedAnswers([]); // Reset answers for the next question
                }

            } catch (error) {
                console.error("Error submitting answer:", error);
                setExternalErrorMessage("An error occurred while submitting your answer. Please try again.");
            }

        } else if (actionType === "Previous") {
            setExternalErrorMessage(null);

            // 2. Create Payload for previous question
            const { questionData } = createPayloads({}, "Previous");

            try {
                await dispatch(getQuestion(questionData));
            } catch (error) {
                console.error("Error fetching previous question:", error);
                setExternalErrorMessage("Failed to load previous question.");
            }
        }
    }, [
        selectedAnswers,
        question,
        dispatch,
        navigate,
        createPayloads,
        setSelectedAnswers,
        setExternalErrorMessage,
        checkAndTriggerDeclineModal
    ]);

    return {
        selectedAnswers,
        setSelectedAnswers,
        errorMessage: finalErrorMessage,
        onRadioChange,
        onCheckboxChange,
        onQuestionSubmit,
        declineModalInfo,
        handleDeclineSubmit,
        allAnswersDeclined: answersDeclined
    };
}
